import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteWebPushSubscription,
  fetchVapidPublicKey,
  saveWebPushSubscription,
} from "../api/webPushService";
import { urlBase64ToUint8Array } from "../utils/webPushEncoding";
import { addBreadcrumb } from "../config/sentry";

const VAPID_STORAGE_KEY = "compasscare_vapid_public_key";

// Returns the specific reason web push is unsupported, or null if supported.
// Precise reasons make the web_push.unsupported breadcrumb actionable in Sentry
// (#202) — a generic "missing-serviceworker-or-pushmanager" hid which check
// actually failed (e.g. a missing Notification API).
//
// #232: support is determined purely by Push API capability detection — the old
// Chrome-desktop UA gate blocked Safari (installed PWA), Firefox, Edge, and
// Android Chrome from receiving safety-critical fall/offline alerts. The standard
// feature checks below work cross-browser, and "PushManager" in window self-gates
// iOS (the API is only exposed in installed PWAs, not in Safari tabs).
function webPushUnsupportedReason() {
  if (typeof window === "undefined") return "no-window";
  if (!("serviceWorker" in navigator)) return "missing-serviceworker";
  if (!("PushManager" in window)) return "missing-pushmanager";
  if (!("Notification" in window)) return "missing-notification";
  return null;
}

function supportsWebPush() {
  return webPushUnsupportedReason() === null;
}

async function subscribeWithVapid(registration, publicKey) {
  let subscription = await registration.pushManager.getSubscription();
  let storedKey = null;
  try {
    storedKey = sessionStorage.getItem(VAPID_STORAGE_KEY);
  } catch (_err) {
    storedKey = null;
  }
  if (subscription && storedKey && storedKey !== publicKey) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await saveWebPushSubscription(subscription);
  try {
    sessionStorage.setItem(VAPID_STORAGE_KEY, publicKey);
  } catch (_err) {
    // ignore
  }
  // GMA-1318 / #202: make the subscribe success observable in Sentry so QA can
  // confirm the web-push pipe from compasscare-frontend.
  // Search: category:web_push.subscribe.success
  addBreadcrumb("web push subscription saved", {
    category: "web_push.subscribe.success",
    level: "info",
  });
  return subscription;
}

/**
 * GMA-1318 — Chrome desktop Web Push subscribe flow for staff caregivers.
 *
 * Sentry breadcrumbs (#202 — web push "not working" was undiagnosable; only
 * console.error before). After deploy, search compasscare-frontend:
 *   category:web_push.subscribe.success   → subscribe pipe is healthy
 *   category:web_push.subscribe.failed    → subscribe threw (see data.phase/error)
 *   category:web_push.no_vapid_key        → backend returned no VAPID key
 *   category:web_push.permission          → permission outcome (data.permission)
 *   category:web_push.unsupported         → gated off (data.reason)
 *
 * @param {{ enabled: boolean }} options
 */
export default function useWebPushSubscription({ enabled = false } = {}) {
  const subscribeInFlight = useRef(false);
  const [notificationPermission, setNotificationPermission] = useState(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return "unsupported";
    }
    return Notification.permission;
  });
  // GMA: tracks whether a live backend subscription is actually confirmed for
  // this browser — distinct from Notification.permission. A granted permission
  // with a failed/stale subscribe must NOT report as "enabled" (it silently
  // delivered no pushes despite the reassuring banner).
  const [isSubscribed, setIsSubscribed] = useState(false);
  // GMA-1318: false until the initial silent resubscribe attempt has resolved.
  // On refresh with a prior grant, isSubscribed is briefly false while that
  // attempt is in flight; this lets the banner avoid flashing the "re-enable"
  // prompt during that window (it appeared then vanished before QA could click).
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  const refreshPermission = useCallback(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setNotificationPermission("unsupported");
      return;
    }
    setNotificationPermission(Notification.permission);
  }, []);

  const subscribeSilently = useCallback(async () => {
    if (!enabled || !supportsWebPush()) return;
    if (Notification.permission !== "granted") return;
    if (subscribeInFlight.current) return;
    subscribeInFlight.current = true;
    try {
      const registration = await navigator.serviceWorker.ready;
      const publicKey = await fetchVapidPublicKey();
      if (!publicKey) {
        setIsSubscribed(false);
        addBreadcrumb("web push: no VAPID key from server (silent)", {
          category: "web_push.no_vapid_key",
          level: "warning",
          phase: "silent",
        });
        return;
      }
      await subscribeWithVapid(registration, publicKey);
      setIsSubscribed(true);
      refreshPermission();
    } catch (err) {
      setIsSubscribed(false);
      console.error("[WEB_PUSH] Silent resubscribe failed:", err);
      addBreadcrumb("web push silent resubscribe failed", {
        category: "web_push.subscribe.failed",
        level: "warning",
        phase: "silent",
        error: err?.message,
      });
    } finally {
      subscribeInFlight.current = false;
      // GMA-1318: the initial silent check has now resolved (subscribed, failed,
      // or no key) — the banner may stop suppressing the re-enable prompt.
      setSubscriptionChecked(true);
    }
  }, [enabled, refreshPermission]);

  const enableWebPush = useCallback(async () => {
    if (!enabled) return false;
    // Compute the reason once so the gate and the breadcrumb can never diverge
    // if capabilities change between calls (Copilot review, #204).
    const unsupportedReason = webPushUnsupportedReason();
    if (unsupportedReason) {
      addBreadcrumb("web push unsupported on this browser", {
        category: "web_push.unsupported",
        level: "info",
        reason: unsupportedReason,
      });
      return false;
    }
    if (subscribeInFlight.current) return Notification.permission === "granted";

    subscribeInFlight.current = true;
    try {
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      addBreadcrumb("web push permission resolved", {
        category: "web_push.permission",
        level: permission === "granted" ? "info" : "warning",
        permission,
      });
      if (permission !== "granted") {
        setIsSubscribed(false);
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const publicKey = await fetchVapidPublicKey();
      if (!publicKey) {
        setIsSubscribed(false);
        addBreadcrumb("web push: no VAPID key from server (enable)", {
          category: "web_push.no_vapid_key",
          level: "warning",
          phase: "enable",
        });
        return false;
      }
      await subscribeWithVapid(registration, publicKey);
      setIsSubscribed(true);
      refreshPermission();
      return true;
    } catch (err) {
      setIsSubscribed(false);
      console.error("[WEB_PUSH] Enable failed:", err);
      addBreadcrumb("web push enable failed", {
        category: "web_push.subscribe.failed",
        level: "warning",
        phase: "enable",
        error: err?.message,
      });
      refreshPermission();
      return false;
    } finally {
      subscribeInFlight.current = false;
      // GMA-1318: a user-triggered enable attempt has resolved — mark checked.
      setSubscriptionChecked(true);
    }
  }, [enabled, refreshPermission]);

  const unregisterWebPush = useCallback(async () => {
    if (!supportsWebPush()) return;
    try {
      setIsSubscribed(false);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      const endpoint = subscription?.endpoint;
      if (subscription) {
        await subscription.unsubscribe();
      }
      if (endpoint) {
        await deleteWebPushSubscription(endpoint);
      }
    } catch (_err) {
      // logout path is best-effort
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshPermission();
    subscribeSilently();
    return undefined;
  }, [enabled, subscribeSilently, refreshPermission]);

  return {
    enableWebPush,
    unregisterWebPush,
    supportsWebPush: supportsWebPush(),
    notificationPermission,
    isSubscribed,
    subscriptionChecked,
  };
}
